import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  activeCompanyChanged,
  activeTenantChanged,
  availableCompaniesResolved,
  availableTenantsResolved,
} from '@/features/auth/authSlice';
import { useLogout } from '@/features/auth/use-logout';
import { useGetAccountQuery } from '@/features/account/accountApi';
import { AccountMenu } from '@/features/account/account-menu';
import { useCompanyControllerListQuery } from '@/services/generated/api';
import { OnboardingGate } from '@/features/onboarding/onboarding-gate';
import { AppShell } from '@/components/ui/app-shell';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { CompanySelect } from '@/features/companies/company-select';
import { deriveCompanyEnvironment } from '@/features/companies/company-environment';
import { toCompanyOptions } from '@/features/companies/company-options';
import { EnvironmentBadge } from '@/features/companies/environment-badge';
import { SupportChatButton } from '@/features/support/support-chat-button';
import { clearSentryUser, setSentryUser } from '@/lib/sentry';
import { navByGroup } from './nav/nav.config';
import { useNavBadges, withNavBadges } from './nav/use-nav-badges';
import { useCertificationLocks } from './nav/use-certification-locks';
import { deriveOnboardingPhase } from '@/features/onboarding/phase';

/**
 * Key of the sidebar's collapse preference. `AppShell` RECEIVES it by contract
 * (§5.5: the user's preference is RECEIVED, not stored or persisted there), so
 * this layout is what persists it.
 */
const COLLAPSED_STORAGE_KEY = 'factit.appshell.collapsed';

/**
 * `localStorage` can throw, not merely return `null`: in Safari's private
 * browsing with third-party cookies blocked, the access itself throws. A menu
 * width preference is no reason for the whole app not to mount, so both halves
 * are guarded and the default is "expanded".
 */
function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // No persistence: the preference lasts as long as the tab does.
  }
}

/**
 * The frame while the session or the active organisation resolves. It replaces
 * a bare centred «Cargando…» page: the shell stays, so the layout does not jump
 * when the real one arrives, and `PageSkeleton` carries the one `aria-live`
 * «Cargando…» status.
 *
 * The menu is not known yet, so `AppShell` gets `navLoading`: the «Principal»
 * landmark holds a few decorative skeleton rows. Company and account stay
 * empty: no identity is asserted before it is known, and the environment slot
 * holds a badge-sized placeholder so the strip does not collapse (and paints
 * no badge: see the comment on the real slot below). `w-24` is from the spacing
 * scale, as other skeletons use it; no badge-width role exists, and it cannot
 * overflow a 360 px strip the way the 400 px `w-popup-sm` did.
 */
function LoadingShell({ collapsed }: { collapsed: boolean }): React.JSX.Element {
  return (
    <AppShell
      nav={[]}
      navLoading
      collapsed={collapsed}
      environment={<Skeleton variant="text" width="w-24" />}
      company={null}
      account={null}
    >
      <PageSkeleton />
    </AppShell>
  );
}

/**
 * Layout of the routes that need a session. It does three things and nothing
 * else.
 *
 * **One:** it starts the session from the `HttpOnly` cookie with
 * `GET /api/account`. While that resolves the shell stays on screen with a `PageSkeleton` in the
 * content area (`LoadingShell`), a 401
 * redirects to `/login`, and a success serves the account from RTK Query's
 * cache: every consumer of email/tenants reads it with `useGetAccountQuery()`
 * instead of a copy in Redux. The only thing that reaches the auth slice is
 * what the server does not know — the active tenant and company.
 *
 * **Two:** it mounts `AppShell` (§5.5), which is the frame of the 30 views with
 * a session. This used to be a top bar hand-written here, and that bar cost
 * three things nobody could see failing:
 *
 * 1. `AppShell` mounts the application's **only** `<Toaster>`. `useToast()`
 *    publishes into a store outside React, so with nobody subscribed the 24
 *    `useToast()` calls across 19 screens produced nothing visible: every
 *    mutation confirmation — «Reintento encolado», «Credenciales guardadas» —
 *    was silence. Nothing failed and the suite passed, because each test mounts
 *    its own `Toaster` and asserts against the store.
 * 2. The hand-written bar mapped `NAV` ignoring `children`, so the second-level
 *    destinations had no menu entry and four views were unreachable without
 *    typing the URL by hand.
 * 3. `EnvironmentBadge` appeared in no strip at all. The contract demands it
 *    always visible for a concrete reason: not knowing which environment you are
 *    in is how someone issues a real document believing they were testing.
 *
 * **Three:** it attaches to the menu tree the three counters §4.12 asks for —
 * «Recepción», «Conexiones», and «Entregas fallidas». This is the only place they
 * can live: the counts come from hooks, and the `<feature>.nav.ts` files are
 * module-level constants that cannot call any. All three feature files noted it
 * down and none could solve it, for exactly that reason. See
 * `nav/use-nav-badges.ts`.
 */
export function AppLayout(): React.JSX.Element {
  const dispatch = useAppDispatch();
  // `skip` once the session has been ended on purpose, and that guard is load
  // bearing: logging out empties RTK Query's cache while this layout is STILL
  // mounted — the route change react-router schedules is a transition, so it
  // commits after the urgent re-render the empty cache causes — and an
  // un-skipped hook answers an empty cache by asking again. The answer then
  // arrives at a layout that has no session, and the two effects below act on
  // it: the reconciliation CHOOSES an organisation and an empresa (`null` reads
  // as "nothing chosen yet"), which the store's listener writes into
  // `localStorage` for whoever opens this browser next, and `setSentryUser`
  // labels the next person's errors with the previous one's email. Both were
  // reproducible under CI load, about one run in ten
  // (GitHub Actions run 36181738834). Skipping is what makes the layout stop
  // asking; with no account it renders `<Navigate to="/login">` below, which is
  // where it was going anyway.
  const sessionEnded = useAppSelector((state) => state.auth.sessionEnded);
  const { data: account, isLoading, isError } = useGetAccountQuery(undefined, {
    skip: sessionEnded,
  });
  // `skip` stops the next request; this stops the one already in the cache from
  // being read as a session. The entry outlives the flag by a render or two —
  // the reset that empties it is a listener, and listeners run after the
  // reducer — and one render is all the effects below need.
  const data = sessionEnded ? undefined : account;
  const activeTenantId = useAppSelector((state) => state.auth.activeTenantId);
  const activeCompanyId = useAppSelector((state) => state.auth.activeCompanyId);
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);
  // Logging out belongs to the layout for the same reason the account does: it
  // is the one thing all 30 views with a session pass through. `AccountMenu`
  // only reports it.
  const { logout, loggingOut } = useLogout();

  /**
   * Is it already known which organisation each call is aimed at?
   *
   * Every call to the data plane travels with `X-Tenant-Id` (see `baseApi.ts`),
   * and that header comes from `activeTenantId`, which the effect further down
   * fills in when the account arrives. With MORE THAN ONE organisation the
   * backend **rejects** a call with no header (400 `tenant_selection_required`),
   * so a request leaving before that resolution is not a slow request: it is an
   * error.
   *
   * With a single organisation the header is optional for the backend, but the
   * wait happens all the same — one path is easier to reason about than two, and
   * the wait lasts a single render.
   *
   * The check is that the active tenant is one the ACCOUNT names, not merely
   * that there is one. Since the store starts from the selection persisted in
   * this browser (`auth-storage.ts`), an id can arrive from a previous session
   * pointing at an organisation this person no longer belongs to; asking only
   * whether it is non-null would let that id go out in `X-Tenant-Id` and come
   * back 403 before the reconciliation replaces it. Here it costs the same
   * single render it already cost when nothing was restored.
   */
  const tenantReady =
    data?.tenants.some((tenant) => tenant.id === activeTenantId) === true ||
    (data?.tenants.length ?? 0) === 0;

  // The company list feeds the identity strip, which only exists with a
  // session. `skip` while the account has not resolved avoids firing a request
  // the 401 would throw away anyway, and while the tenant is unresolved, one
  // that would come back 400.
  const { data: companies, isLoading: isLoadingCompanies } =
    useCompanyControllerListQuery(undefined, { skip: !data || !tenantReady });
  const companyOptions = toCompanyOptions(companies);

  // The menu in two blocks, with the lock already resolved. The phase comes from
  // the same company list that already feeds the strip: there is no extra
  // request. Memoized because `navByGroup` returns a new array on every call and
  // `AppShell` has effects that depend on `nav`'s identity.
  const phase = deriveOnboardingPhase(companies);
  // The active role's permissions, for the per-role lock. They come from the
  // SAME `GET /api/account` that already feeds the identity strip: there is no
  // extra request, and the matrix keeps living in a single place (the backend).
  const permissions = useMemo(
    () =>
      new Set<string>(
        data?.tenants.find((t) => t.id === activeTenantId)?.permissions ?? [],
      ),
    [data, activeTenantId],
  );
  const baseNav = useMemo(
    () => navByGroup(phase, (permission) => permissions.has(permission)),
    [phase, permissions],
  );

  // §4.12's three counters («Recepción», «Conexiones», «Entregas fallidas»). They
  // go here and not in the `<feature>.nav.ts` files because they come from hooks
  // and those files are module-level constants — see `use-nav-badges.ts`. `skip`
  // while the account has not resolved, for the same reason as the company list:
  // a 401 would throw them away anyway.
  const badges = useNavBadges(Boolean(data));
  const badgedNav = useMemo(() => withNavBadges(baseNav, badges), [baseNav, badges]);
  // The steps of «Certificación» the SII has not reached yet are shown locked. Read
  // only for whoever administers the setup, the same permission the entry asks for.
  const nav = useCertificationLocks(badgedNav, Boolean(data) && permissions.has('settings:write'));

  /**
   * Reconciles the identity strip against what the server returns, handing each
   * reconciliation the preference the account publishes as its DEFAULT.
   *
   * Those two ids are client state, so on a browser that has never seen this
   * account there is nothing to restore and the slice has to choose. Left to
   * itself it chose the first of the list; `lastTenantId` / `lastCompanyId`
   * (written by `PUT /api/account/selection`, see `store.ts`) make it choose
   * where this person actually was — from any device, which the browser's own
   * copy in `localStorage` cannot do. They never override a valid local choice:
   * see the reducers.
   */
  useEffect(() => {
    if (data) {
      dispatch(
        availableTenantsResolved({
          ids: data.tenants.map((t) => t.id),
          preferredId: data.lastTenantId,
        }),
      );
    }
  }, [data, dispatch]);

  useEffect(() => {
    // `activeTenantId` in the guard, not only in the dependencies: a company
    // belongs to an organisation, so with none active there is nothing to
    // reconcile against. Without it, logging out — which nulls both ids — re-ran
    // this effect while the list was still in the cache and CHOSE a company
    // again, leaving the id behind in `localStorage` for the next person on the
    // browser.
    if (companies && activeTenantId !== null) {
      dispatch(
        availableCompaniesResolved({
          ids: companies.map((company) => company.id),
          // The empresa remembered for the organisation that is active NOW, not
          // for `lastTenantId`: after switching organisation this is what returns
          // the strip to the empresa that was open in the incoming one.
          preferredId:
            data?.tenants.find((t) => t.id === activeTenantId)?.lastCompanyId ?? null,
        }),
      );
    }
  }, [companies, data, activeTenantId, dispatch]);

  /**
   * Puts a name and a company on whatever Sentry reports from here on.
   *
   * It lives in this layout and not in each screen because this is the only
   * place all 30 views with a session pass through, and because here is where it
   * is already known who came in: the account has just resolved. It runs again
   * when the organisation or the company changes — an error after switching
   * company belongs to the NEW company, and labelling it with the previous one
   * sends the reader to look at the wrong data.
   */
  useEffect(() => {
    // An error from `GET /api/account` is the session having ended (the 401
    // further down redirects to /login). What Sentry reports after that no
    // longer belongs to this person or this tenant.
    if (isError) {
      clearSentryUser();
      return;
    }
    if (!data) return;
    setSentryUser({
      email: data.email,
      activeTenantId,
      activeCompanyId,
      sendEmail: import.meta.env.VITE_SENTRY_SEND_USER_EMAIL === 'true',
    });
  }, [data, isError, activeTenantId, activeCompanyId]);

  if (isLoading) {
    return <LoadingShell collapsed={collapsed} />;
  }

  if (isError || !data) {
    return <Navigate to="/login" replace />;
  }

  /**
   * The `<Outlet/>` does not mount until there is an active organisation, and
   * that is not cosmetic: **it is the bug that came back from production**.
   * Child screens fire their queries on mount, and a child's effects run BEFORE
   * the parent's, so with the Outlet mounted in the same commit the account
   * arrives in, those queries leave without `X-Tenant-Id` and the backend
   * rejects them with a 400. The same happens to a tab left open from before a
   * deploy. The symptom was identical across every view —
   * «No se pudo completar la solicitud» — and in the onboarding wizard it
   * appeared over the «Enviar a certificación» dialog, as if there were
   * something wrong with the form data.
   *
   * Waiting one render cuts off the whole class of race, instead of fixing it
   * screen by screen with a `skip` the next screen will forget.
   */
  if (!tenantReady) {
    return <LoadingShell collapsed={collapsed} />;
  }

  const activeCompany = companyOptions.find((company) => company.id === activeCompanyId) ?? null;

  return (
    <AppShell
      nav={nav}
      collapsed={collapsed}
      onCollapsedChange={(next) => {
        setCollapsed(next);
        writeCollapsedPreference(next);
      }}
      environment={
        activeCompany ? (
          <EnvironmentBadge
            environment={deriveCompanyEnvironment(activeCompany)}
            size="full"
          />
        ) : (
          // With no active company NO badge is painted: the badge asserts an
          // environment, and asserting «Certificación» while the list loads is
          // exactly the lie the contract makes it mandatory against. It says
          // there is no company, which is what is actually known.
          <span className="text-body-s text-fg-muted">
            {isLoadingCompanies ? 'Cargando empresas…' : 'Sin empresa'}
          </span>
        )
      }
      company={
        <CompanySelect
          // The strip has no visible labels, and a `combobox` does not take its
          // name from its content: without this the control is announced as its
          // own value — «99632700-0 · RUT 99.632.700-0 · Sandbox» — never
          // saying what that value is of.
          aria-label="Empresa activa"
          // The active company's `EnvironmentBadge` sits right to the left of
          // this control, so the closed label does not repeat the environment.
          // The open list does keep showing it per option: there each company
          // can be in a different environment.
          environmentAlreadyVisible
          // Ghost trigger: at `h-9 min-h-touch` (48px) `field`'s border
          // coincides exactly with this header's own `h-row` (48px) edges.
          // See `Select`'s `variant` doc comment (`components/ui/select.tsx`).
          variant="header"
          companies={companyOptions}
          value={activeCompanyId}
          onChange={(companyId) => dispatch(activeCompanyChanged(companyId))}
          disabled={isLoadingCompanies || companyOptions.length === 0}
        />
      }
      actions={<SupportChatButton />}
      account={
        <AccountMenu
          email={data.email}
          tenants={data.tenants}
          activeTenantId={activeTenantId}
          onTenantChange={(tenantId) => dispatch(activeTenantChanged(tenantId))}
          onLogout={logout}
          loggingOut={loggingOut}
        />
      }
    >
      {/*
        The entry sequence is mandatory, and mandatory means there is nowhere
        else to go: every route of the product renders inside this, so typing
        `/get-started` lands on the step that is owed rather than past it. What
        is owed is the server's answer, not this client's — see
        `features/onboarding/onboarding-gate.tsx`.
      */}
      <OnboardingGate>
        <Outlet />
      </OnboardingGate>
    </AppShell>
  );
}
